import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  type ElementRef,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { mapPinDiscSvg } from '../core/domain/catalog';
import type { Coordinates } from '../core/domain/geo';

type Leaflet = typeof import('leaflet');
type LeafletLayer = import('leaflet').Layer;
type LeafletMap = import('leaflet').Map;

/** Aperçu produit dans la mini-fiche d'un marqueur. */
export interface MapMarkerProduct {
  readonly name: string;
  readonly priceLabel: string;
  readonly antiWastePercent?: number;
}

/**
 * Contenu riche de la bulle (recherche carte).
 *
 * Absent sur les marqueurs simples (ex. fiche producteur) : on retombe alors
 * sur `label` + `caption`.
 */
export interface MapMarkerCard {
  /** Chemin interne Angular, ex. `/producteur/…`. */
  readonly href: string;
  readonly meta: string;
  readonly categories?: string;
  readonly ratingLabel?: string;
  readonly tierLabel?: string;
  readonly pickup?: string;
  readonly products?: readonly MapMarkerProduct[];
  readonly ctaLabel?: string;
  /** Photo d’accueil boutique : fond de bulle avec voile. */
  readonly heroUrl?: string;
}

/**
 * Point posé sur la carte.
 *
 * Volontairement sans lien avec `Producer` : la carte ne connaît que des points
 * nommés, si bien qu'une fiche producteur, un secteur ou demain un point de
 * retrait s'y affichent sans que ce composant apprenne un nouveau vocabulaire.
 */
export interface MapMarker {
  readonly id: string;
  readonly label: string;
  /** Ligne secondaire de la bulle légère : commune, distance, créneau. */
  readonly caption?: string;
  /** Mini-fiche détaillée ; prioritaire sur `caption` quand elle est fournie. */
  readonly card?: MapMarkerCard;
  /**
   * Habillage de la pastille (familles de produits + anti-gaspi).
   * Absent → vert cageot uni.
   */
  readonly pin?: {
    readonly colors: readonly string[];
    readonly antiWaste?: boolean;
  };
  readonly coordinates: Coordinates;
  /** Rang affiché dans la pastille, pour relier un point à sa carte en liste. */
  readonly rank?: number;
  readonly highlighted?: boolean;
}

/**
 * Carte des points d'un écran, en fond OpenStreetMap.
 *
 * Leaflet et ses tuiles n'arrivent qu'au premier affichage réel de la carte,
 * par import dynamique : la bibliothèque pèse plus que tout le reste de
 * l'application réunie, et la majorité des visites ne la demandent jamais. Le
 * fond de carte reste OpenStreetMap plutôt qu'un fournisseur à clé — pas de
 * compte à gérer, pas de quota à surveiller, et une attribution suffit.
 *
 * Les marqueurs sont dessinés en CSS (`divIcon`) et non avec les images
 * livrées par Leaflet : rien à recopier dans les assets, et les pastilles
 * suivent la palette de la marque.
 *
 * La sélection d'un point est renvoyée au parent. Le lien « Voir la boutique »
 * de la mini-fiche est navigué ici via le routeur Angular (les bulles Leaflet
 * sont du HTML brut, hors templates).
 */
@Component({
  selector: 'ct-map',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div #canvas class="canvas" role="region" [attr.aria-label]="label()"></div>
    @if (!ready()) {
      <p class="pending ct-muted">Chargement de la carte…</p>
    }
  `,
  styles: `
    /* Leaflet exige une hauteur résolue, sinon il ne dessine rien. Le plancher
       est ici et non chez l'appelant : une carte invisible parce qu'un parent
       n'a pas de hauteur définie est un défaut très long à diagnostiquer. Les
       écrans restent libres de poser leur propre hauteur par-dessus.
       /* isolation retient les z-index internes de Leaflet (panes à 400, contrôles
       à 1000) : sans cela, la carte passe par-dessus l'en-tête collant. */
    :host {
      position: relative;
      z-index: 0;
      isolation: isolate;
      display: block;
      min-height: 12rem;
      overflow: hidden;
      border: 1px solid var(--ct-border);
      border-radius: var(--ct-radius-lg);
      background: var(--ct-neutral);
    }

    /* Hors flux : la toile remplit l'hôte sans dépendre de la résolution d'un
       pourcentage de hauteur, qui échoue dès que le parent est en hauteur
       automatique. */
    .canvas {
      position: absolute;
      inset: 0;
    }

    .pending {
      position: absolute;
      inset: 0;
      display: grid;
      place-items: center;
      font-size: var(--ct-size-sm);
      pointer-events: none;
    }
  `,
})
export class CtMap {
  private readonly router = inject(Router);

  readonly markers = input<readonly MapMarker[]>([]);

  /** Position du client, marquée à part. Absente tant qu'elle est inconnue. */
  readonly center = input<Coordinates | undefined>(undefined);

  /** Cercle du rayon de recherche, dessiné autour de `center` quand les deux existent. */
  readonly radiusKm = input<number | undefined>(undefined);

  /** Libellé du point central (GPS vs commune déclarée). */
  readonly centerTitle = input('Votre position');

  readonly label = input('Carte des producteurs');

  /** Identifiant du point choisi, à charge du parent d'en faire quelque chose. */
  readonly selected = output<string>();

  private readonly canvas = viewChild<ElementRef<HTMLElement>>('canvas');

  protected readonly ready = signal(false);

  private leaflet: Leaflet | undefined;
  private map: LeafletMap | undefined;
  private overlays: LeafletLayer[] = [];
  private resize: ResizeObserver | undefined;
  private readonly onPopupNavigate = (event: Event): void => {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }
    const link = target.closest('a[data-ct-map-nav]');
    if (!(link instanceof HTMLAnchorElement)) {
      return;
    }
    const href = link.getAttribute('href');
    if (href === null || href.length === 0) {
      return;
    }
    event.preventDefault();
    void this.router.navigateByUrl(href);
  };

  /**
   * Signature des points déjà cadrés. Le cadrage automatique ne se rejoue que
   * lorsque la géographie change : sans cela, le moindre survol ramènerait la
   * vue à sa position initiale et rendrait la carte impossible à explorer.
   */
  private framed = '';

  constructor() {
    inject(DestroyRef).onDestroy(() => {
      this.map?.getContainer().removeEventListener('click', this.onPopupNavigate);
      this.resize?.disconnect();
      this.map?.remove();
    });

    effect(() => {
      // Lectures inconditionnelles : l'effet doit suivre ces entrées même quand
      // Leaflet n'est pas encore chargé et que `draw` ne fera rien.
      this.markers();
      this.center();
      this.radiusKm();
      this.centerTitle();
      this.draw();
    });

    effect(() => {
      const host = this.canvas()?.nativeElement;
      if (host !== undefined && this.map === undefined) {
        void this.create(host);
      }
    });
  }

  private async create(host: HTMLElement): Promise<void> {
    const leaflet = await loadLeaflet();
    // La vue a pu être détruite pendant le chargement du module.
    if (this.canvas()?.nativeElement !== host) {
      return;
    }

    this.leaflet = leaflet;
    this.map = leaflet.map(host, {
      // Le pincement et le glissé restent actifs ; seule la molette est neutre,
      // sinon un défilement de page au-dessus de la carte zoomerait la carte.
      scrollWheelZoom: false,
      zoomControl: true,
      attributionControl: true,
    });

    leaflet
      .tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 18,
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      })
      .addTo(this.map);

    // La carte peut naître dans un conteneur encore replié — bascule de vue,
    // onglet caché. Sans cela Leaflet garde une taille nulle et n'affiche rien.
    this.resize = new ResizeObserver(() => this.map?.invalidateSize());
    this.resize.observe(host);

    this.map.getContainer().addEventListener('click', this.onPopupNavigate);

    this.ready.set(true);
    this.draw();
  }

  private draw(): void {
    const leaflet = this.leaflet;
    const map = this.map;
    if (leaflet === undefined || map === undefined) {
      return;
    }

    for (const overlay of this.overlays) {
      overlay.remove();
    }
    this.overlays = [];

    const center = this.center();
    const markers = this.markers();
    const radiusKm = this.radiusKm();

    if (center !== undefined && radiusKm !== undefined) {
      this.overlays.push(
        leaflet
          .circle([center.latitude, center.longitude], {
            radius: radiusKm * 1000,
            color: '#2f5233',
            weight: 1,
            dashArray: '4 4',
            fillColor: '#2f5233',
            fillOpacity: 0.05,
          })
          .addTo(map),
      );
    }

    if (center !== undefined) {
      this.overlays.push(
        leaflet
          .marker([center.latitude, center.longitude], {
            icon: leaflet.divIcon({
              className: 'ct-map-me',
              html: '<span></span>',
              iconSize: [16, 16],
              iconAnchor: [8, 8],
            }),
            keyboard: false,
            interactive: false,
            title: this.centerTitle(),
          })
          .addTo(map),
      );
    }

    let highlightedPin: import('leaflet').Marker | undefined;
    const hoverTips = canHoverTips();

    for (const marker of markers) {
      const pinClasses = [
        'ct-map-pin',
        'leaflet-div-icon',
        marker.highlighted ? 'ct-map-pin--on' : '',
        marker.pin?.antiWaste ? 'ct-map-pin--gaspi' : '',
      ]
        .filter((part) => part.length > 0)
        .join(' ');

      const pin = leaflet
        .marker([marker.coordinates.latitude, marker.coordinates.longitude], {
          icon: leaflet.divIcon({
            className: pinClasses,
            html: pinHtml(marker),
            iconSize: [32, 32],
            iconAnchor: [16, 16],
          }),
          // Pas de `title` natif : le tooltip maison le remplace sur bureau.
          alt: marker.label,
        })
        .addTo(map);

      pin.bindPopup(popupHtml(marker), {
        closeButton: true,
        offset: [0, -6],
        maxWidth: 280,
        className:
          marker.card === undefined
            ? undefined
            : marker.card.heroUrl !== undefined && marker.card.heroUrl.length > 0
              ? 'ct-map-popup-shell ct-map-popup-shell--hero'
              : 'ct-map-popup-shell',
      });

      if (hoverTips) {
        pin.bindTooltip(tooltipHtml(marker), {
          direction: 'top',
          offset: [0, -16],
          opacity: 1,
          className: 'ct-map-tooltip',
          // Permanent false : apparition au survol uniquement.
          permanent: false,
          sticky: false,
        });
        // La mini-fiche au clic suffit : on masque le survol tant qu'elle est ouverte.
        pin.on('popupopen', () => pin.closeTooltip());
      }

      pin.on('click', () => this.selected.emit(marker.id));
      if (marker.highlighted) {
        highlightedPin = pin;
      }
      this.overlays.push(pin);
    }

    // Le clic met à jour `highlighted`, ce qui redessine les pastilles et
    // détruirait sinon la bulle qu'on vient d'ouvrir.
    highlightedPin?.openPopup();

    this.frame(markers, center);
  }

  private frame(markers: readonly MapMarker[], center: Coordinates | undefined): void {
    const leaflet = this.leaflet;
    const map = this.map;
    if (leaflet === undefined || map === undefined) {
      return;
    }

    const points: [number, number][] = markers.map((marker) => [
      marker.coordinates.latitude,
      marker.coordinates.longitude,
    ]);
    if (center !== undefined) {
      points.push([center.latitude, center.longitude]);
    }

    const key = points.map((point) => point.join(',')).join('|');
    if (key === this.framed) {
      return;
    }
    this.framed = key;

    if (points.length === 0) {
      // Cadre national : la carte reste lisible avant toute donnée, sans
      // désigner une région que rien ne justifie.
      map.setView([46.6, 2.5], 5);
      return;
    }
    if (points.length === 1) {
      map.setView(points[0]!, 13);
      return;
    }
    map.fitBounds(leaflet.latLngBounds(points), { padding: [28, 28], maxZoom: 14 });
  }
}

/**
 * Leaflet 1.x est publié en UMD, sans champ `exports`. Selon l'interop du
 * bundler, l'espace de noms est le module lui-même ou son export par défaut.
 */
async function loadLeaflet(): Promise<Leaflet> {
  const loaded = (await import('leaflet')) as Leaflet & { default?: Leaflet };
  return loaded.default ?? loaded;
}

function popupHtml(marker: MapMarker): string {
  if (marker.card !== undefined) {
    return cardPopupHtml(marker.label, marker.card);
  }
  const caption =
    marker.caption === undefined
      ? ''
      : `<span class="ct-map-popup__caption">${escapeHtml(marker.caption)}</span>`;
  return `<div class="ct-map-popup"><strong>${escapeHtml(marker.label)}</strong>${caption}</div>`;
}

/** Pastille : disque (uni ou camembert) + rang. */
function pinHtml(marker: MapMarker): string {
  const rank = marker.rank === undefined ? '' : String(marker.rank);
  return `${mapPinDiscSvg(marker.pin?.colors ?? [])}<span class="ct-map-pin__rank">${escapeHtml(rank)}</span>`;
}

/** Survol bureau : nom + lieu, sans reprendre toute la mini-fiche. */
function tooltipHtml(marker: MapMarker): string {
  const meta = marker.card?.meta ?? marker.caption;
  const metaLine =
    meta === undefined || meta.length === 0
      ? ''
      : `<span class="ct-map-tooltip__meta">${escapeHtml(meta)}</span>`;
  return `<span class="ct-map-tooltip__body"><strong class="ct-map-tooltip__name">${escapeHtml(marker.label)}</strong>${metaLine}</span>`;
}

function cardPopupHtml(name: string, card: MapMarkerCard): string {
  const trust = [card.tierLabel, card.ratingLabel].filter(
    (part): part is string => typeof part === 'string' && part.length > 0,
  );
  const products =
    card.products === undefined || card.products.length === 0
      ? ''
      : `<ul class="ct-map-popup__products">${card.products
          .map((product) => {
            const badge =
              product.antiWastePercent === undefined
                ? ''
                : `<span class="ct-map-popup__gaspi">−${escapeHtml(String(product.antiWastePercent))} %</span>`;
            return `<li><span class="ct-map-popup__product-name">${escapeHtml(product.name)}${badge}</span><span class="ct-map-popup__price">${escapeHtml(product.priceLabel)}</span></li>`;
          })
          .join('')}</ul>`;

  const categories =
    card.categories === undefined || card.categories.length === 0
      ? ''
      : `<span class="ct-map-popup__categories">${escapeHtml(card.categories)}</span>`;
  const trustLine =
    trust.length === 0
      ? ''
      : `<span class="ct-map-popup__trust">${escapeHtml(trust.join(' · '))}</span>`;
  const pickup =
    card.pickup === undefined || card.pickup.length === 0
      ? ''
      : `<span class="ct-map-popup__pickup">Retrait : ${escapeHtml(card.pickup)}</span>`;
  const cta = escapeHtml(card.ctaLabel ?? 'Voir la boutique');
  const hero =
    card.heroUrl !== undefined && card.heroUrl.length > 0
      ? ` ct-map-popup--hero" style="--ct-map-hero: ${cssUrl(card.heroUrl)}`
      : '';

  return `<div class="ct-map-popup ct-map-popup--card${hero}">
    <strong class="ct-map-popup__name">${escapeHtml(name)}</strong>
    <span class="ct-map-popup__meta">${escapeHtml(card.meta)}</span>
    ${trustLine}
    ${categories}
    ${products}
    ${pickup}
    <a class="ct-map-popup__cta" href="${escapeHtml(card.href)}" data-ct-map-nav>${cta} <span aria-hidden="true">→</span></a>
  </div>`;
}

/** Les bulles sont construites en HTML : tout texte de données y est échappé. */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function cssUrl(value: string): string {
  return `url('${value.replace(/\\/g, '\\\\').replace(/'/g, "\\'")}')`;
}

/** Tooltip de survol : souris fine uniquement (évite le conflit tactile / clic). */
function canHoverTips(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(hover: hover) and (pointer: fine)').matches
  );
}
